// Shared helpers for giving fenced code blocks their own hover "Copy" button
// wherever markdown is rendered via v-html (Agent chat, Core/Markdown, etc).
// Because v-html output isn't tracked by Vue, the button markup is built as a
// plain HTML string at render time (see `renderCodeBlockWithCopyButton`) and
// its clicks are handled via event delegation (see `onCodeCopyClick`) instead
// of per-button Vue listeners.
//
// The matching CSS lives in ./code-copy-buttons.css and is imported here (not
// in each consuming component) so every usage shares one definition.
import './code-copy-buttons.css'

const ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;',
}

export function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>"']/g, char => ESCAPE_MAP[char])
}

const COPY_ICON = '<svg class="code-copy-icon size-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>'

const CHECK_ICON = '<svg class="code-copy-check size-3.5 hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>'

const COPY_BUTTON_CLASS = 'code-copy-btn absolute right-2 top-2 flex cursor-pointer items-center justify-center rounded-lg p-1.5 text-charcoal-300 opacity-0 transition-opacity duration-150 hover:bg-charcoal-700/60 hover:text-white group-hover/code:opacity-100 focus-visible:opacity-100'

// Wraps a fenced code block's markup with a container carrying a hover-visible
// Copy button. `codeText` must already be markdown-escaped for safe innerHTML
// use; `lang` (optional) becomes the code element's language class so any
// existing syntax-highlighting hooks keep working.
export function renderCodeBlockWithCopyButton(codeText, lang) {
  const languageClass = lang ? ` class="language-${escapeHtml(lang)}"` : ''

  return '<div class="code-copy-wrapper group/code relative">'
    + `<pre><code${languageClass}>${codeText}</code></pre>`
    + `<button type="button" class="${COPY_BUTTON_CLASS}" data-code-copy aria-label="Copy code" title="Copy code">${COPY_ICON}${CHECK_ICON}</button>`
    + '</div>'
}

// Delegated click handler: attach to any ancestor of v-html'd markdown content,
// e.g. `@click="onCodeCopyClick"` on the container, or `onClick: onCodeCopyClick`
// on an `h()`-created vnode. Looks up the button's sibling `<code>` element,
// copies its text, then briefly flips the button into a "copied" state purely
// via DOM classList since this content lives outside Vue's reactivity.
export function onCodeCopyClick(event) {
  const button = event.target?.closest?.('[data-code-copy]')
  if (!button) return

  const codeEl = button.parentElement?.querySelector('pre code')
  if (!codeEl || !navigator.clipboard) return

  navigator.clipboard.writeText(codeEl.textContent ?? '').then(() => {
    button.classList.add('is-copied')
    clearTimeout(button._copyResetTimeout)
    button._copyResetTimeout = setTimeout(() => button.classList.remove('is-copied'), 1500)
  }).catch(() => {
    // Clipboard access can be denied/unavailable; copy is a non-critical nice-to-have.
  })
}
